/**
 * `sibyl recheck <pr>` — the second half of the product, wired up.
 *
 * ⭐ THIS IS THE HALF THAT LIFTS THE BLOCK. `sibyl review` raises findings and a person accepts the
 * ones that matter; those accepted findings are what held the pull request. Then the author pushes,
 * and somebody has to say whether what they pushed actually cleared it. src/engine/rereview.ts has
 * decided that since it was written and NOTHING CALLED IT — a reviewer that blocks and then cannot
 * tell you whether you have fixed it is worse than one that never blocked, and until this file
 * existed that is exactly what shipped.
 *
 * ⛔ THIS FILE MAKES NO JUDGEMENT ABOUT RESOLUTION. Same rule as src/cli/review.ts: it resolves a
 * credential, prepares a clone, reads the queue and hands the pieces to `reReview`. Every question
 * of the form "is this fixed" is answered behind that call, where it is tested against fabricated
 * trees. A condition written here would be a rule with no test — and worse, a SECOND opinion about
 * resolution competing with the one the mutation gate pins.
 *
 * ⛔ "THE AUTHOR TOUCHED THAT FILE" IS NOT RESOLUTION, AND THERE IS NO SHORTCUT HERE THAT SAYS
 * OTHERWISE. No path in this file reads a changed sha, a changed line, a moved anchor or a file
 * appearing in the changed set as a fix. `findMechanism` is the only authority, and the mutant
 * `touching-the-file-reads-as-resolution` in scripts/mutation-gate.mjs exists because skipping the
 * cited file in that search lifts a block that still holds.
 *
 * ⛔ A PRIOR FINDING WHOSE QUOTE CANNOT BE RECOVERED IS `undecidable`, NEVER `resolved`. This is the
 * sharpest edge in the file. `findMechanism` returns undefined for an empty quote, and undefined
 * plus a touched file reads as RESOLVED — so handing `reReview` a blank quote silently lifts every
 * block on every file the author touched. Such a finding is refused before it reaches the module,
 * counted, printed, and still blocks. Refusing to lift is the safe error; lifting wrongly is not.
 *
 * ⚠️ A `TriageRecord` deliberately carries no quote (src/triage/store.ts: the record is the durable
 * decision, the evidence belongs to one review of one sha). So the quote is recovered from the tree
 * at `lastSeenSha`, which is the sha `lastAnchor` was recorded against. When that object is no
 * longer in the reused clone — a force-push, a shallow fetch — the finding is `undecidable` rather
 * than guessed at. `priorQuoteResolver` is one function and one seam: the day the store carries the
 * quote, it reads the field and the git call goes away.
 *
 * ⛔ NOTHING IS POSTED AND NOTHING IS WRITTEN. No review, no comment, no commit status, no change to
 * the queue. What WOULD be posted is printed instead. Posting is a separate, gated step —
 * `triage.require_human` is the gate and src/github/post.ts is the door.
 *
 * ⛔ EXIT 0 WHEN THE HARNESS WORKED, INCLUDING WHEN EVERY FINDING STILL BLOCKS. The exit code
 * answers "did the recheck run", never "is the pull request clean" — the same rule the entry point
 * and src/cli/review.ts carry, for the same reason. 69 is a backend, credential or clone problem.
 *
 * See docs/ARCHITECTURE.md, "Re-review and unblocking".
 */

import { execFile } from "node:child_process"
import { readFileSync } from "node:fs"
import type { SibylConfig } from "../config/schema.js"
import { EXIT } from "../config/validate.js"
import { GitHub } from "../github/pr.js"
import { prepareClone, resolveInClone, CloneError, type PreparedClone } from "../repo/clone.js"
import { openTriageStore, type TriageRecord } from "../triage/store.js"
import {
  reReview,
  replyBody,
  type Citation,
  type NewTree,
  type PriorFinding,
  type ReReviewDecision,
  type ReReviewOutcome,
} from "../engine/rereview.js"
import type { Finding, Severity } from "../types/finding.js"
import { credentialFrom } from "./review.js"

export const RECHECK_USAGE = `sibyl recheck <pr> [--dry-run] [--json]

  --dry-run   decide and print, change nothing
  --json      the same decisions, machine-readable
`

// ── What the command reaches the world through ─────────────────────────────────────────────────

/**
 * One row of the queue, with the evidence the record itself does not keep.
 *
 * ⚠️ `quote` is `undefined` when it could not be recovered. That is a fact about our records, not a
 * fact about the code, and the two must never be confused — see the ⛔ in the header.
 */
export interface PriorRecord {
  readonly record: TriageRecord
  readonly quote: string | undefined
}

/**
 * Everything outside this process.
 *
 * ⭐ Injected rather than imported, for the same reason `ReviewDeps` is: the whole decision path has
 * to be exercisable against a fabricated tree with no network, no clone and no model. A re-review
 * that can only be tested by pushing a commit to a real pull request is one that gets tested once.
 */
export interface RecheckDeps {
  /** The clone at the CURRENT head of the pull request. Nothing is decided against anything else. */
  readonly prepareClone: (repo: string, pr: number) => Promise<PreparedClone>
  /** The queue, with each record's quote resolved. Called after the clone, which it may read. */
  readonly priorRecords: (clone: PreparedClone) => Promise<readonly PriorRecord[]>
  /**
   * The files these commits touched.
   *
   * ⚠️ This BOUNDS THE SEARCH and decides nothing — `NewTree.changedPaths` says so in those words.
   * The live wiring hands over the pull request's whole changed-file list, which is a superset of
   * "since the last review": a superset only ever widens the hunt for a mechanism, which is the
   * safe direction. It cannot widen `resolved`, because a finding that passed the `introducedHere`
   * gate cites a file that is in that list already.
   */
  readonly changedPaths: (repo: string, pr: number) => Promise<readonly string[]>
  /** Post-image lines of a path in the prepared clone, or undefined when the file is gone. */
  readonly readLines: (clonePath: string, path: string) => readonly string[] | undefined
  /**
   * A fresh review pass over the new commits, when one is wanted.
   *
   * ⚠️ Absent by default, and its absence is STAMPED on the output rather than swallowed: with no
   * new findings, `changed_shape` is unreachable, and a defect standing in the old one's place
   * reads as `resolved` with nothing raised against it. That is a real gap in coverage and it is
   * named in the report.
   */
  readonly newFindings?: (clone: PreparedClone) => Promise<readonly Finding[]>
  readonly log?: (line: string) => void
}

// ── The result ─────────────────────────────────────────────────────────────────────────────────

/**
 * ⛔ `undecidable` IS NOT A FOURTH OUTCOME. `rereview.ts` fixes the list at three and this file does
 * not extend it. It is the marker for a finding that never reached the module at all, and it is
 * kept visibly separate precisely so it can never be counted as a lift.
 */
export type RecheckOutcome = ReReviewOutcome | "undecidable"

export interface RecheckDecision {
  readonly fingerprint: string
  readonly title: string
  readonly severity: Severity
  /** From `severity.actions[…].blocks_merge` in the resolved config. */
  readonly blocksMerge: boolean
  readonly outcome: RecheckOutcome
  /** Plain language, from `reReview`. Never reworded here. */
  readonly reason: string
  /** ⛔ The line that decided it, byte-for-byte, so a person can check the reviewer's work. */
  readonly citation?: Citation
  /** Where the mechanism was hunted. The evidence behind a `resolved`, which cites no line. */
  readonly searched: readonly string[]
  /** What the posting step would put on this thread. Printed here; posted nowhere. */
  readonly wouldPost?: string
  /** ⛔ Unresolved AND at a severity that blocks. This is the only field the verdict reads. */
  readonly stillBlocks: boolean
}

export interface RecheckResult {
  readonly repo: string
  readonly pr: number
  readonly headSha: string
  readonly ref: string
  readonly usedFallbackRef: boolean
  readonly prior: {
    readonly total: number
    readonly accepted: number
    readonly dismissedNotReRaised: number
    readonly untriaged: number
  }
  readonly counts: {
    readonly decided: number
    readonly resolved: number
    readonly notResolved: number
    readonly changedShape: number
    readonly undecidable: number
    readonly stillBlocking: number
    readonly unresolvedButNotBlocking: number
  }
  /** ⛔ True only when nothing a person accepted still holds at a severity that blocks the merge. */
  readonly wouldUnblock: boolean
  /**
   * The status a posting step would set.
   *
   * ⚠️ `reReview` owns this and it is STRICTER than `wouldUnblock`: it refuses to flip while any
   * prior finding still holds, whatever its severity. Both numbers are printed rather than
   * reconciled, because they answer different questions and hiding either one is how a green status
   * ends up over an unanswered finding.
   */
  readonly commitStatus: "success" | "failure"
  readonly decisions: readonly RecheckDecision[]
  /** Coverage this recheck did not have, which a reader would otherwise assume it did. */
  readonly gaps: readonly string[]
  /** ⛔ Always true today: this command has no write path at all. See the header. */
  readonly dryRun: true
}

// ── Arguments ──────────────────────────────────────────────────────────────────────────────────

export interface RecheckArgs {
  readonly pr?: number
  readonly json: boolean
  readonly dryRun: boolean
  /** Set when the arguments cannot be read. The caller prints it and exits USAGE. */
  readonly error?: string
}

/**
 * ⛔ An unknown flag is refused, never ignored. A silently ignored flag is a setting the user
 * believes is in effect — the same reasoning src/cli/index.ts applies to an unknown flag and
 * src/config/schema.ts applies to an unknown config key.
 */
export function parseRecheckArgs(argv: readonly string[]): RecheckArgs {
  let json = false
  let dryRun = false
  let pr: number | undefined
  for (const a of argv) {
    if (a === "--json") json = true
    else if (a === "--dry-run") dryRun = true
    else if (a.startsWith("-")) return { json, dryRun, error: `unknown flag ${a}` }
    else if (pr === undefined) {
      const n = Number(a)
      // ⛔ Validated here, before it is rendered into a ref path by the clone. An unvalidated number
      // becomes `refs/pull/../../heads/main/merge` and fetches something nobody asked to review.
      if (!Number.isSafeInteger(n) || n <= 0) {
        return { json, dryRun, error: `\`sibyl recheck\` needs a pull-request number, got ${JSON.stringify(a)}` }
      }
      pr = n
    } else return { json, dryRun, error: `unexpected argument ${JSON.stringify(a)}` }
  }
  return { ...(pr === undefined ? {} : { pr }), json, dryRun }
}

// ── Reading the tree ───────────────────────────────────────────────────────────────────────────

/**
 * Every line of a file in the prepared clone.
 *
 * ⛔ THE PATH GUARD IS `resolveInClone`, REUSED RATHER THAN REWRITTEN. It is the only thing standing
 * between a path that arrived from a model and `/etc/passwd`, and a second copy of it here would be
 * a second thing to get wrong. A path escape THROWS — it is an attack or a bug, and reporting it as
 * "that file was not there" would let it read as a resolution.
 *
 * ⚠️ The line-splitting mirrors `readFileAt` in src/repo/clone.ts exactly: the trailing element of a
 * newline-terminated file is dropped, and a CRLF terminator's CR is part of the terminator and not
 * of the content. `recheck.test.ts` pins the two against each other, because two readers that
 * disagree by one invisible byte would fail every quote comparison for a reason nobody can see.
 */
export function readLinesAt(clonePath: string, path: string): readonly string[] | undefined {
  const abs = resolveInClone(clonePath, path)
  let text: string
  try {
    text = readFileSync(abs, "utf8")
  } catch {
    return undefined
  }
  const lines = text.split("\n")
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop()
  return lines.map((l) => (l.endsWith("\r") ? l.slice(0, -1) : l))
}

/** Memoised so a mechanism hunt across forty changed files reads each of them once. */
function memoReadLines(
  read: (clonePath: string, path: string) => readonly string[] | undefined,
): (clonePath: string, path: string) => readonly string[] | undefined {
  const cache = new Map<string, readonly string[] | undefined>()
  return (clonePath, path) => {
    // ⛔ A SOURCE ESCAPE, NOT A RAW NUL. The byte is identical at runtime — it is a separator that
    // cannot occur in a path — but written literally it makes `file` report the source as `data`,
    // makes grep skip the file entirely (so the symbol defined here reads as dead code), and makes
    // `git diff` show `Bin`. This repository has an invariant asserting no raw C0 byte exists in
    // source, and this line is what it caught on 2026-09-15, in code that had already been pushed.
    const key = `${clonePath}\u0000${path}`
    if (cache.has(key)) return cache.get(key)
    let lines: readonly string[] | undefined
    try {
      lines = read(clonePath, path)
    } catch (e) {
      // ⚠️ A path escape on a SEARCH path costs that path, not the run. The cited path is checked
      // separately and by name, where a refusal is the point rather than a lost file.
      if (!(e instanceof CloneError)) throw e
      lines = undefined
    }
    cache.set(key, lines)
    return lines
  }
}

// ── Recovering the quote a record does not keep ────────────────────────────────────────────────

function gitShow(cwd: string, spec: string): Promise<string | undefined> {
  return new Promise((done) => {
    // ⛔ An argument array, never a shell string. A path and a sha both arrive from a stored record;
    // one of them containing a backtick in a shell string is code execution on the reviewer's box.
    execFile(
      "git",
      ["-c", "core.hooksPath=/dev/null", "-C", cwd, "show", spec],
      { encoding: "utf8", maxBuffer: 64 * 1024 * 1024, windowsHide: true, env: { ...process.env, LC_ALL: "C" } },
      (err, stdout) => done(err === null ? stdout : undefined),
    )
  })
}

/**
 * The line a prior finding quoted, byte-exact, or undefined.
 *
 * ⭐ Read at `lastSeenSha`, which is the sha `lastAnchor` was recorded against — the quote gate
 * guarantees the finding's quote was byte-identical to that line at that sha. Reading the same
 * line number at the NEW head would quote whatever happens to sit there now, which is a different
 * line of code wearing the finding's name.
 *
 * ⚠️ The `quote` field is preferred when a record carries one. The store does not today; the check
 * is structural so that the day it does, this function stops spawning git and nothing else moves.
 */
export function priorQuoteResolver(clonePath: string): (r: TriageRecord) => Promise<string | undefined> {
  const files = new Map<string, readonly string[] | undefined>()
  return async (r: TriageRecord): Promise<string | undefined> => {
    const carried = (r as { quote?: unknown }).quote
    if (typeof carried === "string" && carried !== "") return carried
    const key = `${r.lastSeenSha}:${r.lastAnchor.path}`
    if (!files.has(key)) {
      const text = await gitShow(clonePath, key)
      if (text === undefined) files.set(key, undefined)
      else {
        const lines = text.split("\n")
        if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop()
        files.set(
          key,
          lines.map((l) => (l.endsWith("\r") ? l.slice(0, -1) : l)),
        )
      }
    }
    const line = files.get(key)?.[r.lastAnchor.line - 1]
    return line === undefined || line.trim() === "" ? undefined : line
  }
}

// ── A stored record, as the module wants it ────────────────────────────────────────────────────

/**
 * ⚠️ THE PROSE FIELDS ARE RECONSTRUCTED AND THEY ARE NOT THE ORIGINAL. A `TriageRecord` keeps the
 * decision, not the evidence. `reReview` reads identity, anchor and quote and nothing else, so what
 * is filled in here never reaches a judgement — but it must never be printed at an author either,
 * which is why the rendering below quotes `record.title` and the module's own `reason`.
 */
function asPriorFinding(r: TriageRecord, quote: string): PriorFinding {
  const finding: Finding = {
    fingerprint: r.fingerprint,
    dimensionId: r.dimensionId,
    severity: r.severity,
    cls: "introduced",
    title: r.title,
    anchor: r.lastAnchor,
    quote,
    consequence: "",
    failureScenario: "",
    recommendedFix: "",
    effort: "M",
    reasoning: "",
    components: {
      citedLineExists: true,
      quoteIsByteIdentical: true,
      introducedHere: true,
      survivedRefutation: true,
      hasFailureScenario: false,
      ruleCitationChecksOut: false,
    },
    provenance: {
      backend: "claude_cli",
      model: "",
      effort: "",
      reviewedSha: r.lastSeenSha,
      usedFallbackRef: false,
    },
  }
  return { finding, state: r.state }
}

/** `severity.actions[sev].blocks_merge`, which is the config's own answer and not ours. */
function blocksMerge(cfg: SibylConfig, sev: Severity): boolean {
  return cfg.severity.actions[sev]?.blocksMerge === true
}

/**
 * The floor for NEW findings on a re-review, taken from the config rather than assumed.
 *
 * ⭐ "Later passes report blocking findings only" — a one-line fix reaching round seven on style is
 * how a reviewer gets muted. The lowest severity that blocks the merge IS that floor, so changing
 * `blocks_merge` changes both together and they cannot drift.
 */
function newFindingFloor(cfg: SibylConfig): Severity {
  for (const sev of ["low", "medium", "high"] as const) if (blocksMerge(cfg, sev)) return sev
  return "high"
}

/** The paths `findMechanism` will read, in the order it reads them. Description, never a decision. */
function searchOrder(citedPath: string, changedPaths: readonly string[]): readonly string[] {
  return [citedPath, ...changedPaths.filter((p) => p !== citedPath)]
}

// ── The decision ───────────────────────────────────────────────────────────────────────────────

export interface RecheckOptions {
  readonly repo: string
  readonly pr: number
  readonly config: SibylConfig
  readonly deps: RecheckDeps
}

/**
 * Decide, per accepted prior finding, whether the new commits cleared it.
 *
 * ⛔ Every judgement in here is `reReview`'s. This function chooses WHICH findings to ask about and
 * HOW to report the answers, and nothing else.
 */
export async function recheck(opts: RecheckOptions): Promise<RecheckResult> {
  const { repo, pr, config: cfg, deps } = opts
  const log = deps.log ?? ((): void => {})
  const gaps: string[] = []

  log(`preparing the clone at the current head of ${repo}#${pr}`)
  const clone = await deps.prepareClone(repo, pr)
  if (clone.usedFallbackRef) {
    gaps.push("the merge ref was unborn, so this is the branch and not the merge result — a block lifted here is lifted against the branch")
  }

  const changedPaths = await deps.changedPaths(repo, pr)
  const readLines = memoReadLines(deps.readLines)
  const tree: NewTree = {
    headSha: clone.sha,
    changedPaths,
    readFile: (path: string) => readLines(clone.path, path),
  }

  const records = await deps.priorRecords(clone)
  // ⛔ A finding nobody triaged never blocked anything, so there is nothing here to lift. Counted
  // and stated rather than dropped — a queue that quietly shows part of itself reads as a short one.
  const untriaged = records.filter((p) => p.record.state.kind === "todo")
  const considered = records.filter((p) => p.record.state.kind !== "todo")

  // ⛔ A record with no recoverable quote NEVER reaches `reReview`. An empty quote makes
  // `findMechanism` return undefined, and undefined on a touched file reads as RESOLVED.
  const undecidable = considered.filter(
    (p) => p.record.state.kind === "accepted" && (p.quote === undefined || p.quote.trim() === ""),
  )
  const refused = new Set(undecidable.map((p) => p.record.fingerprint))
  const decidable = considered.filter((p) => !refused.has(p.record.fingerprint))

  const newFindings = deps.newFindings === undefined ? [] : await deps.newFindings(clone)
  if (deps.newFindings === undefined) {
    gaps.push(
      "no fresh review pass ran, so nothing new was raised — a defect standing in a cleared one's place would read as `resolved` with no replacement named (`changed_shape` is unreachable without one)",
    )
  }

  const result = reReview({
    // ⚠️ The only member of `decidable` that can lack a quote is one a human DISMISSED, and
    // `reReview` returns on the dismissal before it looks at the quote. The fallback is a floor
    // under that reasoning, not a path anything is decided on.
    prior: decidable.map((p) => asPriorFinding(p.record, p.quote ?? "")),
    newFindings,
    tree,
    suppressBelow: newFindingFloor(cfg),
  })

  const byFingerprint = new Map<string, ReReviewDecision>()
  for (const d of result.decisions) byFingerprint.set(d.fingerprint, d)

  const decisions: RecheckDecision[] = []
  for (const p of decidable) {
    const d = byFingerprint.get(p.record.fingerprint)
    // ⛔ A human dismissed it. `reReview` refused to re-decide it and so does this; it is counted
    // under `dismissedNotReRaised` and never appears as an outcome.
    if (d === undefined) continue
    const blocks = blocksMerge(cfg, p.record.severity)
    decisions.push({
      fingerprint: p.record.fingerprint,
      title: p.record.title,
      severity: p.record.severity,
      blocksMerge: blocks,
      outcome: d.outcome,
      reason: d.reason,
      ...(d.citation === undefined ? {} : { citation: d.citation }),
      searched: searchOrder(p.record.lastAnchor.path, changedPaths),
      wouldPost: replyBody(d),
      stillBlocks: d.outcome === "not_resolved" && blocks,
    })
  }

  for (const p of undecidable) {
    const blocks = blocksMerge(cfg, p.record.severity)
    decisions.push({
      fingerprint: p.record.fingerprint,
      title: p.record.title,
      severity: p.record.severity,
      blocksMerge: blocks,
      outcome: "undecidable",
      reason:
        `the line this finding quoted could not be read back at ${p.record.lastSeenSha.slice(0, 7)}, so there is no mechanism to hunt for — ` +
        `it is left standing rather than guessed at`,
      searched: [],
      stillBlocks: blocks,
    })
  }

  const count = (o: RecheckOutcome): number => decisions.filter((d) => d.outcome === o).length
  const stillBlocking = decisions.filter((d) => d.stillBlocks).length
  const unresolvedButNotBlocking = decisions.filter(
    (d) => (d.outcome === "not_resolved" || d.outcome === "undecidable") && !d.stillBlocks,
  ).length

  // ⛔ `reReview` owns the commit status and it is stricter than the merge verdict. An undecidable
  // finding is outside its view, so it can only ever be downgraded here — never up.
  const commitStatus: "success" | "failure" =
    result.commitStatus === "success" && undecidable.length === 0 ? "success" : "failure"

  return {
    repo,
    pr,
    headSha: clone.sha,
    ref: clone.ref,
    usedFallbackRef: clone.usedFallbackRef,
    prior: {
      total: records.length,
      accepted: considered.filter((p) => p.record.state.kind === "accepted").length,
      dismissedNotReRaised: considered.filter((p) => p.record.state.kind === "dismissed").length,
      untriaged: untriaged.length,
    },
    counts: {
      decided: decisions.length,
      resolved: count("resolved"),
      notResolved: count("not_resolved"),
      changedShape: count("changed_shape"),
      undecidable: count("undecidable"),
      stillBlocking,
      unresolvedButNotBlocking,
    },
    wouldUnblock: stillBlocking === 0,
    commitStatus,
    decisions,
    gaps,
    dryRun: true,
  }
}

// ── Rendering ──────────────────────────────────────────────────────────────────────────────────

const MARK: Record<RecheckOutcome, string> = {
  resolved: "✅",
  not_resolved: "⛔",
  changed_shape: "🔀",
  undecidable: "⚠️ ",
}

/** At most this many search paths are listed before the rest are counted. */
const MAX_SEARCH_SHOWN = 6

function describeSearch(d: RecheckDecision): string {
  if (d.searched.length === 0) return ""
  const shown = d.searched.slice(0, MAX_SEARCH_SHOWN)
  const rest = d.searched.length - shown.length
  const where = rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ")
  return `looked in ${d.searched.length} file${d.searched.length === 1 ? "" : "s"} — ${where}`
}

/**
 * The recheck, as a person reads it.
 *
 * ⛔ EVERY COUNT CARRIES ITS DENOMINATOR, and every verdict carries the line that produced it. A
 * `resolved` with nothing behind it is indistinguishable from a coin flip, so a resolution prints
 * the rule it applied AND where it hunted, and anything still holding prints the line byte-for-byte.
 */
export function renderRecheck(r: RecheckResult): string {
  const L: string[] = []
  const n = r.counts.decided
  L.push(`${r.repo}#${r.pr} — re-review at ${r.headSha.slice(0, 7)} (${r.ref})${r.usedFallbackRef ? " ⚠️ fallback ref" : ""}`)
  L.push(
    `  ${r.prior.total} record${r.prior.total === 1 ? "" : "s"} in the queue: ` +
      `${r.prior.accepted} accepted, ${r.prior.dismissedNotReRaised} dismissed and not re-raised, ${r.prior.untriaged} not yet triaged`,
  )
  L.push("")

  const groups: readonly (readonly [RecheckOutcome, string])[] = [
    ["resolved", "Resolved"],
    ["changed_shape", "Changed shape"],
    ["not_resolved", "Still holding"],
    ["undecidable", "Could not be decided"],
  ]
  for (const [outcome, heading] of groups) {
    const rows = r.decisions.filter((d) => d.outcome === outcome)
    if (rows.length === 0) continue
    L.push(`${heading} ${rows.length} of ${n}`)
    for (const d of rows) {
      L.push(`  ${MARK[d.outcome]} ${d.severity.toUpperCase().padEnd(6)} ${d.title}  [${d.fingerprint.slice(0, 12)}]`)
      L.push(`     ${d.reason}`)
      if (d.citation !== undefined) {
        L.push(`     ${d.citation.path}:${d.citation.line}`)
        L.push(`       ${d.citation.text}`)
      }
      const search = describeSearch(d)
      if (search !== "") L.push(`     ${search}`)
    }
    L.push("")
  }

  L.push(
    `${r.counts.resolved} of ${n} resolved · ${r.counts.notResolved} of ${n} still holding · ` +
      `${r.counts.changedShape} of ${n} changed shape · ${r.counts.undecidable} of ${n} undecided`,
  )
  if (r.wouldUnblock) {
    L.push(`✅ the pull request WOULD be unblocked — 0 of ${n} still hold at a severity that blocks the merge`)
  } else {
    L.push(`⛔ the pull request would NOT be unblocked — ${r.counts.stillBlocking} of ${n} still hold at a severity that blocks the merge`)
  }
  if (r.counts.unresolvedButNotBlocking > 0) {
    L.push(
      `   ${r.counts.unresolvedButNotBlocking} of ${n} are unresolved at a severity that does not block the merge (severity.actions blocks_merge = false)`,
    )
  }
  L.push(`   the commit status a posting step would set is \`${r.commitStatus}\``)

  if (r.gaps.length > 0) {
    L.push("")
    L.push(`⚠️  Not covered (${r.gaps.length}):`)
    for (const g of r.gaps) L.push(`  ${g}`)
  }

  const postable = r.decisions.filter((d) => d.wouldPost !== undefined)
  L.push("")
  L.push(`Nothing was posted and nothing was written. ${postable.length} of ${n} repl${postable.length === 1 ? "y" : "ies"} would be posted:`)
  for (const d of postable) {
    L.push(`  [${d.fingerprint.slice(0, 12)}] ${(d.wouldPost as string).split("\n")[0] ?? ""}`)
  }
  return L.join("\n")
}

// ── The command ────────────────────────────────────────────────────────────────────────────────

export interface RecheckCommandOptions {
  readonly config: SibylConfig
  /** Everything after `recheck`, including the pull-request number. This file owns its own flags. */
  readonly argv: readonly string[]
  readonly repo?: string
  readonly env?: NodeJS.ProcessEnv
  readonly out?: (s: string) => void
  readonly err?: (s: string) => void
  /** Injected whole, for tests. Absent means the live clone, GitHub and triage store. */
  readonly deps?: RecheckDeps
}

/**
 * ⛔ Exit 0 when the harness worked, INCLUDING when every prior finding still blocks. Findings are
 * not an error; only a broken stage is, and that is 69 because it is a backend, credential or clone
 * problem rather than a configuration one.
 */
export async function runRecheckCommand(opts: RecheckCommandOptions): Promise<number> {
  const env = opts.env ?? process.env
  const out = opts.out ?? ((s: string) => process.stdout.write(s))
  const err = opts.err ?? ((s: string) => process.stderr.write(s))
  const cfg = opts.config

  const args = parseRecheckArgs(opts.argv)
  if (args.error !== undefined) {
    err(`⛔ ${args.error}\n\n${RECHECK_USAGE}`)
    return EXIT.USAGE
  }
  if (args.pr === undefined) {
    err(`⛔ \`sibyl recheck\` needs a pull-request number.\n\n${RECHECK_USAGE}`)
    return EXIT.USAGE
  }

  const repo = opts.repo ?? cfg.repo.slug
  if (repo === "") {
    err("⛔ no repository. Pass `--repo owner/name` or set `slug` under [repo].\n")
    return EXIT.USAGE
  }

  let deps = opts.deps
  let close = (): void => {}
  if (deps === undefined) {
    const keys = cfg.secrets.keys.filter((k) => k.includes("GITHUB") || k.includes("GH_"))
    const token = credentialFrom(keys, env)
    if (token === undefined) {
      err(`⛔ no GitHub token. Set one of ${keys.join(", ")}.\n`)
      return EXIT.UNAVAILABLE
    }
    let store: Awaited<ReturnType<typeof openTriageStore>>
    try {
      store = await openTriageStore({ path: cfg.triage.db })
    } catch (e) {
      err(`⛔ the triage queue could not be opened: ${e instanceof Error ? e.message : String(e)}\n`)
      return EXIT.UNAVAILABLE
    }
    err(`triage: ${store.backend.kind} at ${store.backend.path} — ${store.backend.reason}\n`)
    const github = new GitHub({ token: token.value })
    close = (): void => store.close()
    deps = {
      prepareClone: (r, n) => prepareClone(cfg, r, n, token.value),
      changedPaths: async (r, n) => (await github.changedFiles(r, n)).map((f) => f.path),
      priorRecords: async (clone) => {
        const quoteOf = priorQuoteResolver(clone.path)
        return Promise.all(store.all().map(async (record) => ({ record, quote: await quoteOf(record) })))
      },
      readLines: readLinesAt,
      log: (line) => err(`   ${line}\n`),
    }
  }

  try {
    const result = await recheck({ repo, pr: args.pr, config: cfg, deps })
    if (args.json) out(`${JSON.stringify(result, null, 2)}\n`)
    else {
      out(`${renderRecheck(result)}\n`)
      // ⚠️ `--dry-run` is accepted and is already the only mode: this command has no write path and
      // no network write. Said out loud rather than ignored, because a flag that appears to do
      // nothing and a flag that is silently dropped look identical from the outside.
      if (args.dryRun) out(`\n--dry-run: this command never writes and never posts, so it is already a dry run.\n`)
      out(`\nTo post the replies above, run the posting step — it is gated on \`triage.require_human\`.\n`)
    }
    // ⛔ Findings that still block are the ANSWER, not a failure. Exit 0.
    return EXIT.OK
  } catch (e) {
    err(`⛔ ${e instanceof Error ? e.message : String(e)}\n`)
    return EXIT.UNAVAILABLE
  } finally {
    close()
  }
}
